/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-imports */
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Warning as WarningIcon,
} from '@material-ui/icons';

import { FormatDate } from 'components';
import PropTypes from 'prop-types';
import { contigoService } from 'services';
import { API_URL } from 'settings';

import { useStyles } from './styles';

export function FileUploadDialog({ open, onClose, orderId }) {
  const classes = useStyles();

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFile, setNewFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      loadAttachments();
    }
  }, [open, orderId]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      const response = await contigoService.listOrderAttachments(orderId);
      setAttachments(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
      toast.error('Erro ao carregar anexos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result.split(',')[1] || reader.result;
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setNewFile(event.target.files[0]);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      setNewFile(event.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleRemoveFile = () => {
    setNewFile(null);
  };

  const handleSubmit = async () => {
    if (!fileName || !newFile) {
      toast.error('Preencha o nome do arquivo e selecione um arquivo');
      return;
    }

    setIsSubmitting(true);

    try {
      const base64File = await handleFileToBase64(newFile);
      const fileExtension = newFile.name.split('.').pop() || '';

      await contigoService.uploadOrderAttachment({
        order_id: orderId,
        file: base64File,
        file_name: fileName,
        file_extension: fileExtension,
      });

      toast.success('Upload concluído com sucesso');
      setFileName('');
      setNewFile(null);
      loadAttachments();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Erro ao subir o arquivo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = (attachment) => {
    setAttachmentToDelete(attachment);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAttachmentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!attachmentToDelete) return;

    try {
      setDeleting(true);
      await contigoService.deleteOrderAttachment(orderId, attachmentToDelete.id);
      toast.success('Anexo excluído com sucesso');
      loadAttachments();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir anexo:', error);
      toast.error('Erro ao excluir anexo');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = (attachment) => {
    const url = `${API_URL}${attachment.file_path}`;
    window.open(url, '_blank');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle className={classes.title}>
        <Box display='flex' alignItems='center'>
          <AttachFileIcon style={{ marginRight: 8 }} />
          <Typography variant='h6'>Gerenciar Arquivos</Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Paper
          className={classes.uploadArea}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => document.getElementById('file-input-contigo').click()}
        >
          <input
            id='file-input-contigo'
            type='file'
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <CloudUploadIcon className={classes.uploadIcon} />
          <Typography variant='h6'>Arraste arquivos ou clique para selecionar</Typography>
          <Typography variant='body2' color='textSecondary'>
            Formatos suportados: PDF, JPG, PNG, DOCX, XLSX
          </Typography>
        </Paper>

        <TextField
          label='Nome do Arquivo'
          variant='outlined'
          fullWidth
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          className={classes.formField}
          required
        />

        {newFile && (
          <Box mt={2}>
            <Typography variant='subtitle1'>Arquivo selecionado:</Typography>
            <List dense>
              <ListItem className={classes.fileItem}>
                <ListItemIcon>
                  <FileIcon color='primary' />
                </ListItemIcon>
                <ListItemText
                  primary={newFile.name}
                  secondary={`${(newFile.size / 1024).toFixed(2)} KB`}
                />
                <IconButton edge='end' onClick={handleRemoveFile}>
                  <DeleteIcon color='error' />
                </IconButton>
              </ListItem>
            </List>
          </Box>
        )}

        <Divider style={{ margin: '16px 0' }} />

        <Typography variant='subtitle1'>Arquivos já importados:</Typography>
        {loading ? (
          <Typography variant='body2' color='textSecondary' style={{ marginTop: 8 }}>
            Carregando...
          </Typography>
        ) : attachments?.length > 0 ? (
          <Paper className={classes.fileList}>
            <List>
              {attachments.map((attachment) => (
                <ListItem key={attachment.id} className={classes.fileItem}>
                  <ListItemIcon>
                    <CheckCircleIcon className={classes.successIcon} />
                  </ListItemIcon>
                  <ListItemText
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleDownload(attachment)}
                    title={`${attachment.file_name}.${attachment.file_extension}`}
                    primary={`${attachment.file_name}.${attachment.file_extension}`}
                    secondary={
                      <>
                        Criado em: <FormatDate>{attachment.created_at}</FormatDate>
                        <div>Adicionado por: {attachment.user_name}</div>
                      </>
                    }
                  />
                  <IconButton edge='end' onClick={() => handleOpenDeleteDialog(attachment)}>
                    <DeleteIcon color='error' />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography variant='body2' color='textSecondary' style={{ marginTop: 8 }}>
            Nenhum arquivo importado ainda.
          </Typography>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        <Button onClick={onClose} color='primary'>
          Fechar
        </Button>
        <Button
          onClick={handleSubmit}
          color='primary'
          variant='contained'
          disabled={!fileName || !newFile || isSubmitting}
        >
          {isSubmitting ? 'Enviando...' : 'Enviar Arquivo'}
        </Button>
      </DialogActions>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={1}>
            <WarningIcon color='error' />
            Confirmar Exclusão
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Tem certeza que deseja excluir este anexo?</Typography>
          {attachmentToDelete && (
            <Box mt={2} p={2} bgcolor='#f5f5f5' borderRadius={1}>
              <Typography variant='body2'>
                <strong>Arquivo:</strong> {attachmentToDelete.file_name}.
                {attachmentToDelete.file_extension}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Adicionado por: {attachmentToDelete.user_name}
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Data: <FormatDate>{attachmentToDelete.created_at}</FormatDate>
              </Typography>
            </Box>
          )}
          <Typography variant='body2' color='error' style={{ marginTop: 16 }}>
            Esta ação não poderá ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color='secondary'
            variant='contained'
            disabled={deleting}
          >
            {deleting ? 'Excluindo...' : 'Confirmar Exclusão'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

FileUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderId: PropTypes.number.isRequired,
};
